import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
    title: ReactNode
    subtitle?: ReactNode
    back?: string
    actions?: ReactNode
    children?: ReactNode
    className?: string
}

const hasInAppHistory = (): boolean => {
    const state = window.history.state as { idx?: number } | null
    return (state?.idx ?? 0) > 0
}

export const BackButton: FC<{ fallback: string, label?: string }> = ({ fallback, label = 'Go back' }): ReactNode => {
    const router = useRouter()

    return (
        <button
            type='button'
            aria-label={label}
            onClick={() => {
                if (hasInAppHistory()) router.back()
                else void router.push(fallback)
            }}
            className='-ml-2 grid size-11 shrink-0 place-items-center rounded-full text-foreground transition-colors hover:bg-slate-900/5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
        >
            <ArrowLeft className='size-[22px]' aria-hidden='true' />
        </button>
    )
}

export const PageHeader: FC<PageHeaderProps> = ({
    title,
    subtitle,
    back,
    actions,
    children,
    className
}): ReactNode => {
    return (
        <header
            className={cn(
                'sticky top-0 z-30 border-b border-border/70 bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur-md print:hidden',
                className
            )}
        >
            <div className='mx-auto flex h-14 max-w-6xl items-center gap-2 px-4 lg:h-16 lg:px-8'>
                {back && <BackButton fallback={back} />}

                <div className='min-w-0 flex-1'>
                    <h1 className='truncate font-heading text-lg leading-tight font-bold text-foreground lg:text-xl'>
                        {title}
                    </h1>

                    {subtitle && (
                        <p className='truncate text-xs font-medium text-muted-foreground'>
                            {subtitle}
                        </p>
                    )}
                </div>

                {actions && (
                    <div className='flex shrink-0 items-center gap-1.5'>
                        {actions}
                    </div>
                )}
            </div>

            {children && (
                <div className='mx-auto max-w-6xl px-4 pb-3 lg:px-8'>
                    {children}
                </div>
            )}
        </header>
    )
}

export const PageContainer: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => {
    return (
        <div className={cn('mx-auto w-full max-w-6xl px-4 py-4 lg:px-8 lg:py-6', className)}>
            {children}
        </div>
    )
}
